/*************************************** THIRD-PARTY MODULES **************************************/
import React from 'react';
import {City, CityInstance} from '@canimmunize/javelinscript';

/***************************************** PROJECT MODULES ****************************************/
import {ReactAlgoliaSearch} from './algolia-component';

/******************************************* COMPONENTS *******************************************/
/**
 * Export autocomplete components
 */
export * from './algolia-component';
export * from './autocomplete-component';

interface CitySearchProps extends React.HTMLAttributes<HTMLInputElement> {
    value: string;
    region?: string;
    onSelected: (city: CityInstance) => void;

    /**
     * Object to configure basic (simple === match) client-side filtering
     *
     * resultObjPath: the path to dig into on the returned object
     * allowedMatches: array of items to allow for the value at the property at
     *                 resultObjPath
     *
     * Example:
     *     allowedResults value:
     *       {resultObjPath: 'name', allowedMatches: ['jo', 'jen', 'jack']}
     *     Data:
     *       [{name: 'jo', id: 1}, {name: 'jen', id: 2}, {name: 'smit', id: 3}]
     *     Values allowed in the output:
     *       [{name: 'jo', id: 1}, {name: 'jen', id: 2}]
     */
    allowedResults?: {resultObjPath: string; allowedMatches: any[]};

    /**
     * Actual props for wrapper Algolia dropdown wrapper component
     *
     * Example:
     *     wrapperProps={{
     *         className: cn(`ci-card-content-width`),
     *         style: {color: `red`}
     *     }}
     */
    wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
}

/**
 * Wrapper around Algolia search component to simplify props for city index
 *
 * All usual react component props can also be supplied i.e. onFocus, onClick
 * These props will be passed to the inner input element
 */
export const CitySearch = (props: CitySearchProps) => {
    const {value, region, onSelected, allowedResults, wrapperProps, ...inputProps} = props;
    const filters = region ? `r:${region.toUpperCase()}` : ``;
    const displayKey = region ? `n` : city => `${city.n}, ${city.r}`;
    return (
        <ReactAlgoliaSearch
            displayKey={displayKey}
            filters={filters}
            defaultValue={value}
            indexName={`Cities`}
            inputProps={inputProps}
            allowedResults={props.allowedResults}
            wrapperProps={wrapperProps}
            onSelected={(_value, item) => {
                if (!item) {
                    onSelected(null);
                } else {
                    const {cityId, n, r, c, o, a} = item;
                    onSelected(new City(cityId, n, r, c, o, a));
                }
            }}
        />
    );
};

CitySearch.displayName = `CitySearch`;
